// NSB resolved-item bridge (Part 1 → Part 2 handoff).
//
// Part 1 (item matching + item intel) is CAST IN CONCRETE — we never change its matching logic.
// When Part 1 finishes a match (exact, or the user's pick) it produces a FINAL item_name_to_use.
// Part 1 passively emits that result here via setResolvedItemNameToUse(); the matching logic is
// untouched — this is just a read-out of the result.
//
// The map key is the line-item INPUT name (the text the user typed / Part 1 searched with). It is
// used ONLY to pair the result back to the right row — NSB never joins on the input. NSB joins on
// the VALUE (the final item_name_to_use) against the precompute name_sku grain.

const resolvedByInput = new Map<string, string>();
const subscribersByInput = new Map<string, Set<() => void>>();
// Inputs whose resolution came from an explicit user PICK (a multi-match item where the buyer chose
// one), as opposed to a deterministic single-match auto-resolve. Picks must NOT survive a re-upload
// of the same name (the buyer has to pick again); auto-resolves are deterministic and may persist.
const pickedInputs = new Set<string>();

// Durable per-row resolution (keyed by the stable line-item id) — see the section at the bottom of
// this file for the full rationale. Declared up here so the event-change reset below can clear it.
const resolvedByLineItemId = new Map<string, string>();
const subscribersByLineItemId = new Map<string, Set<() => void>>();

// Notifications are coalesced into a microtask, but PER KEY. A single module-level flag would collapse
// notifications across different keys — row B's would be dropped because row A already scheduled one in
// the same tick, leaving B's cells stuck on their unresolved state forever. So pending keys accumulate
// and one flush notifies each of them exactly once. Bulk writes (hydration over a whole event, an
// event-change reset) therefore cost one subscriber round per row instead of one per write.
const pendingInputKeys = new Set<string>();
const pendingLineItemKeys = new Set<string>();
let flushScheduled = false;

const scheduleFlush = (): void => {
  if (flushScheduled) return;
  flushScheduled = true;
  queueMicrotask(() => {
    flushScheduled = false;
    const inputKeys = Array.from(pendingInputKeys);
    const lineItemKeys = Array.from(pendingLineItemKeys);
    pendingInputKeys.clear();
    pendingLineItemKeys.clear();
    inputKeys.forEach(key => subscribersByInput.get(key)?.forEach(fn => fn()));
    lineItemKeys.forEach(key => subscribersByLineItemId.get(key)?.forEach(fn => fn()));
  });
};

const notifyInput = (key: string): void => {
  if (!subscribersByInput.get(key)?.size) return;
  pendingInputKeys.add(key);
  scheduleFlush();
};

const notifyLineItem = (key: string): void => {
  if (!subscribersByLineItemId.get(key)?.size) return;
  pendingLineItemKeys.add(key);
  scheduleFlush();
};

const normalizeInput = (inputName: string): string => inputName.trim().toLowerCase();

// Called by Part 1 (passive, read-only) with the input it searched and the final item_name_to_use it
// resolved. `fromPick` = true when the buyer chose from the multi-match picker (vs an auto-resolve).
export const setResolvedItemNameToUse = (inputName: string, itemNameToUse: string, fromPick = false): void => {
  const key = normalizeInput(inputName);
  if (!key || !itemNameToUse) return;
  resolvedByInput.set(key, itemNameToUse);
  if (fromPick) pickedInputs.add(key);
  else pickedInputs.delete(key);
  notifyInput(key);
};

// Read by NSB (the Benchmark renderer) for its row's input name → the matched item_name_to_use.
export const getResolvedItemNameToUse = (inputName: string): string | undefined =>
  resolvedByInput.get(normalizeInput(inputName));

// Lets a benchmark cell re-render when Part 1 resolves its row.
export const subscribeResolvedItemNameToUse = (inputName: string, fn: () => void): (() => void) => {
  const key = normalizeInput(inputName);
  if (!subscribersByInput.has(key)) subscribersByInput.set(key, new Set());
  subscribersByInput.get(key)!.add(fn);
  return () => { subscribersByInput.get(key)?.delete(fn); };
};

// Drop every resolution. Called when the event changes so a new event's same-named items start
// unresolved and Part 1 re-resolves them fresh (resolutions are keyed by name, not by event).
// Subscribers are notified so any live cells re-render to their unresolved state.
export const resetResolvedItemNameToUse = (): void => {
  resolvedByInput.clear();
  pickedInputs.clear();
  Array.from(subscribersByInput.keys()).forEach(notifyInput);
  // Also drop the durable per-row resolutions; the new event re-hydrates them on load.
  resolvedByLineItemId.clear();
  Array.from(subscribersByLineItemId.keys()).forEach(notifyLineItem);
};

// Drop only PICK-sourced resolutions, keeping deterministic single-match auto-resolves. Called
// synchronously on a line-item import (re-upload) so a fresh upload of a multi-match name shows the
// picker again, while single-match items still auto-apply (and re-seed before the import auto-save).
//
// LEFT UNCOALESCED, unlike every other notify in this file — but note what does and does not order this
// against the import auto-save. The ordering guarantee comes from the `resolvedByInput.delete` WRITE being
// synchronous, which coalescing would not change; every subscriber here is a useSyncExternalStore handler,
// so notifying only schedules a React re-render either way. It stays uncoalesced because there is nothing
// to gain: the cleared set is bounded by the picks made in one session, so this is never a bulk write.
export const resetPickedResolutions = (): void => {
  const cleared = Array.from(pickedInputs);
  cleared.forEach(key => resolvedByInput.delete(key));
  pickedInputs.clear();
  cleared.forEach(key => subscribersByInput.get(key)?.forEach(fn => fn()));
};

// ─── Durable per-row resolution (keyed by the STABLE line-item id) ──────────────────────────────
//
// The map above is keyed by INPUT name and is in-session only. This second map is the row's DURABLE
// resolution: the value of the `resolved_line_item_name` backend column. It is hydrated on load from
// what the server returned, and updated when the buyer picks. It is keyed by the stable line-item id
// (the DB id, or the client `new-<uuid>` before save) — NOT the input name — so two rows that share
// the same typed name resolve independently, and so a row that already carries a resolution can skip
// Part 1's match search entirely on reload (the performance win). A renderer that has this for its
// row knows: skip the search, show "See Intel", drive NSB off this name, and show it as a tooltip.
// (The two backing Maps are declared near the top of the file so the event-change reset can clear them.)

const idKey = (lineItemId: number | string): string => String(lineItemId);

// Set (or clear, when itemNameToUse is empty) a row's durable resolution and notify its subscribers.
export const setResolvedNameForLineItem = (lineItemId: number | string, itemNameToUse: string): void => {
  const key = idKey(lineItemId);
  if (!key) return;
  if (itemNameToUse) resolvedByLineItemId.set(key, itemNameToUse);
  else resolvedByLineItemId.delete(key);
  notifyLineItem(key);
};

// Read a row's durable resolution (the resolved_line_item_name) by its stable id.
export const getResolvedNameForLineItem = (lineItemId: number | string): string | undefined =>
  resolvedByLineItemId.get(idKey(lineItemId));

// Lets a cell re-render when its row's durable resolution is hydrated or changes.
export const subscribeResolvedNameForLineItem = (lineItemId: number | string, fn: () => void): (() => void) => {
  const key = idKey(lineItemId);
  if (!subscribersByLineItemId.has(key)) subscribersByLineItemId.set(key, new Set());
  subscribersByLineItemId.get(key)!.add(fn);
  return () => { subscribersByLineItemId.get(key)?.delete(fn); };
};

// Seed the durable resolutions from a freshly loaded event (each row's id + its resolved_line_item_name
// as returned by the server). Authoritative: any row not in `entries` (or with an empty value) is
// cleared, so a row whose resolution was removed server-side stops being treated as resolved. Every
// affected row's subscribers are notified so live cells re-render. Also seeds the input-name bridge so
// NSB (which joins by the resolved value) resolves on reload WITHOUT Part 1 having to re-search.
export const hydrateResolvedNamesForLineItems = (
  entries: Array<{ inputName?: string; lineItemId: number | string; resolvedName?: string | null }>,
): void => {
  const affected = new Set<string>(resolvedByLineItemId.keys());
  resolvedByLineItemId.clear();
  entries.forEach(({ inputName, lineItemId, resolvedName }) => {
    const key = idKey(lineItemId);
    if (!key) return;
    affected.add(key);
    if (resolvedName) {
      resolvedByLineItemId.set(key, resolvedName);
      // Mirror into the input-name bridge so the (name-keyed) NSB benchmark resolves on reload.
      if (inputName) setResolvedItemNameToUse(inputName, resolvedName);
    }
  });
  affected.forEach(notifyLineItem);
};
